import { useMemo } from 'react'

import { Field, Input, Select } from '@/components/field'
import type { IdentifierDraft } from '@/features/auth/identifier-draft'
import { useSmsCoverage } from '@/features/auth/sms-coverage'
import { cx } from '@/lib/cx'
import { useLanguage } from '@/lib/i18n/store'
import { useT } from '@/lib/i18n/translate'
import { nationOptions } from '@/lib/nations'

/**
 * The Email | Phone toggle every identifier form shares (T11.3): login,
 * registration, forgot-password. Normalization is the backend's job. Forms
 * that send a code pass `smsHint` so a country outside the SMS allowlist is
 * flagged before a request is spent on it (I1).
 */
export function IdentifierInput({
  value,
  onChange,
  smsHint = false,
  required = true,
}: {
  value: IdentifierDraft
  onChange: (value: IdentifierDraft) => void
  smsHint?: boolean
  /** Off when the identifier is an optional part of a larger form. */
  required?: boolean
}) {
  const t = useT()
  const language = useLanguage()
  const nations = useMemo(() => nationOptions(language), [language])
  const smsCovered = useSmsCoverage(smsHint)
  const hint = smsHint && value.channel === 'phone' && !smsCovered(value.nation) ? t('auth.smsUnsupportedHint') : undefined

  return (
    <div className="flex flex-col gap-4">
      <div role="group" aria-label={t('auth.identifierToggle')} className="border-border grid grid-cols-2 gap-1 rounded-lg border p-1">
        {(['email', 'phone'] as const).map((channel) => (
          <button
            key={channel}
            type="button"
            aria-pressed={value.channel === channel}
            onClick={() => onChange({ ...value, channel })}
            className={cx(
              'min-h-9 rounded-md text-sm font-medium',
              value.channel === channel ? 'bg-accent text-accent-fg' : 'text-fg hover:bg-accent-soft',
            )}
          >
            {t(channel === 'phone' ? 'auth.phone' : 'auth.email')}
          </button>
        ))}
      </div>
      {value.channel === 'phone' ? (
        <>
          <Field label={t('auth.nation')}>
            <Select value={value.nation} onChange={(e) => onChange({ ...value, nation: e.target.value })}>
              {nations.map((nation) => (
                <option key={nation.code} value={nation.code}>
                  {nation.label}
                </option>
              ))}
            </Select>
          </Field>
          <Field label={t('auth.phoneNumber')} hint={hint}>
            <Input
              type="tel"
              inputMode="tel"
              autoComplete="tel"
              required={required}
              value={value.number}
              onChange={(e) => onChange({ ...value, number: e.target.value })}
            />
          </Field>
        </>
      ) : (
        <Field label={t('auth.email')}>
          <Input
            type="email"
            autoComplete="email"
            required={required}
            value={value.email}
            onChange={(e) => onChange({ ...value, email: e.target.value })}
          />
        </Field>
      )}
    </div>
  )
}
